import classnames from 'classnames';
import type { SkillBranch, Vector2 } from '@sorrem/shared';
import type { SkillTreeIntroSchedule } from './skill-tree-intro';
import { SKILL_TREE_GROUP_SIZE, SKILL_TREE_LABEL_SPACE } from './skill-tree-layout';
import type { SkillTreeGroupModel } from './skill-tree-model';
import { SKILL_TREE_EMBLEMS } from './skill-tree-emblems';
import { toGroupSelectionId, type SkillTreeSvgIds } from './skill-tree-ids';
import {
    FRAME_DARK,
    GOLD_FACE,
    GOLD_HIGHLIGHT,
    GOLD_SHADOW,
    IRON,
    IRON_DARK,
    IRON_HIGHLIGHT,
    hexToRgb,
    resolveSkillGroupTint,
} from './skill-tree-palette';
import { SkillTreeLineEmblem } from './SkillTreeLineEmblem';
import { SkillTreeIntroPart } from './SkillTreeIntroPart';
import styles from './SkillTreeScene.module.scss';

interface SkillTreeGroupNodeProps {
    readonly group: SkillTreeGroupModel;
    readonly isHovered: boolean;
    readonly isSelected: boolean;
    /** The scene's SVG def ids (the label shadow, the emblem glow). */
    readonly svgIds: SkillTreeSvgIds;
    /** The Skills tab intro's schedule while it plays: the root pops in as the link from its dial arrives. */
    readonly intro: SkillTreeIntroSchedule | undefined;
}

const HALF = SKILL_TREE_GROUP_SIZE / 2;

/** The dark inside each branch's frame, which an emblem's knockouts are filled with. */
const FRAME_FILL: Record<SkillTreeGroupModel['placement']['branch'], string> = {
    martial: FRAME_DARK,
    essence: FRAME_DARK,
    crafting: IRON_DARK,
};

/** A group's tint lifted toward white, so its label reads over the sky. */
function labelColor(color: string): string {
    const [r, g, b] = hexToRgb(color).map((channel) => Math.round(channel * 0.6 + 255 * 0.4));
    return `rgb(${r}, ${g}, ${b})`;
}

/** Height of a group label's backing pill. */
const LABEL_HEIGHT = 30;

/** Rough width of an uppercase label at the group label's size, for its backing. */
function labelWidth(name: string): number {
    return name.length * 15 + 24;
}

/** Where the Crafting hexagon's rivets sit, one inside each corner. */
const CRAFTING_RIVETS: readonly Vector2[] = [
    { x: 0, y: -31 },
    { x: 27, y: -15.5 },
    { x: 27, y: 15.5 },
    { x: 0, y: 31 },
    { x: -27, y: 15.5 },
    { x: -27, y: -15.5 },
];

const CRAFTING_HEXAGON = 'M0 -38L33 -19L33 19L0 38L-33 19L-33 -19Z';

/** The frame each branch's roots share: Martial a beveled gold diamond, Essence a glowing ring, Crafting a riveted iron hexagon. */
function GroupFrame({ branch, color }: { readonly branch: SkillBranch; readonly color: string }): JSX.Element {
    switch (branch) {
        case 'martial':
            return (
                <g strokeLinejoin="bevel">
                    <path d="M0 -40L40 0L0 40L-40 0Z" fill={FRAME_DARK} stroke={GOLD_SHADOW} strokeWidth={7} />
                    <path d="M0 -40L40 0L0 40L-40 0Z" fill="none" stroke={GOLD_FACE} strokeWidth={4.5} />
                    <path d="M0 -40L40 0L0 40L-40 0Z" fill="none" stroke={GOLD_HIGHLIGHT} strokeWidth={0.9} strokeOpacity={0.75} />
                </g>
            );
        case 'essence':
            return (
                <g>
                    <circle r={34} fill={FRAME_DARK} fillOpacity={0.85} />
                    <circle r={34} fill="none" stroke={color} strokeWidth={6} strokeOpacity={0.35} />
                    <circle r={34} fill="none" stroke={color} strokeWidth={1.6} />
                </g>
            );
        case 'crafting':
            return (
                <g>
                    <path d={CRAFTING_HEXAGON} fill={IRON_DARK} stroke={IRON_DARK} strokeWidth={6} strokeLinejoin="round" />
                    <path d={CRAFTING_HEXAGON} fill="none" stroke={IRON} strokeWidth={3} strokeLinejoin="round" />
                    {CRAFTING_RIVETS.map((rivet) => <circle key={`${rivet.x},${rivet.y}`} cx={rivet.x} cy={rivet.y} r={1.6} fill={IRON_HIGHLIGHT} />)}
                </g>
            );
        default: {
            const unknownBranch: never = branch;
            return unknownBranch;
        }
    }
}

/**
 * A grouping root: the named junction between a dial and its skills, drawn
 * in its branch's materials with an emblem for what it holds and no progress
 * of its own (docs/gameplay/skill-tree-ui.md, Grouping Roots).
 */
export function SkillTreeGroupNode({ group, isHovered, isSelected, svgIds, intro }: SkillTreeGroupNodeProps): JSX.Element {
    const { id, branch, name, position } = group.placement;
    const color = resolveSkillGroupTint(id);
    const emblem = SKILL_TREE_EMBLEMS[id];
    return (
        <SkillTreeIntroPart window={intro?.groups.get(id)?.pop} motion="pop" origin={position}>
            <g className={classnames(styles.groupNode, group.isUnawakened && styles.unawakened)}>
                <g transform={`translate(${position.x} ${position.y})`}>
                    {(isHovered || isSelected) && <circle r={HALF + 8} fill="none" stroke={color} strokeOpacity={isSelected ? 0.9 : 0.45} strokeWidth={isSelected ? 2 : 1.5} />}
                    {isSelected && <circle r={HALF + 13} fill="none" stroke={color} strokeOpacity={0.5} strokeWidth={1} />}
                    <GroupFrame branch={branch} color={color} />
                    {emblem && <SkillTreeLineEmblem layers={emblem} color={color} fill={FRAME_FILL[branch]} glowFilterId={svgIds.emblemGlow} />}
                </g>
                <g transform={`translate(${position.x} ${position.y + HALF + SKILL_TREE_LABEL_SPACE * 0.45 - 4})`}>
                    {/* A dark backing, so the links leaving the root never run through its name. */}
                    <rect
                        className={styles.groupLabelBacking}
                        x={-labelWidth(name) / 2}
                        y={-LABEL_HEIGHT / 2}
                        width={labelWidth(name)}
                        height={LABEL_HEIGHT}
                        rx={LABEL_HEIGHT / 2}
                    />
                    {/* The label is all capitals, so its baseline drops half a cap height below the backing's center. */}
                    <text className={styles.groupLabel} dy="0.35em" fill={labelColor(color)} filter={`url(#${svgIds.textShadow})`}>
                        {name}
                    </text>
                </g>
                <circle className={styles.hitTarget} cx={position.x} cy={position.y} r={HALF} data-node-id={toGroupSelectionId(id)} />
            </g>
        </SkillTreeIntroPart>
    );
}
